import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/api/firebase";

interface NotificationItem {
  id: string;
  type: string;
  recipientUid: string;
  orgId: string;
  orgName: string;
  title: string;
  message: string;
  severity: string;
  metadata: Record<string, unknown>;
  read: boolean;
  readAt?: string;
  createdAt: string;
}

interface NotificationCounts {
  totalUnread: number;
  byOrg: Record<string, number>;
}

export function useNotifications(filters?: { type?: string; unreadOnly?: boolean; limit?: number }) {
  return useQuery({
    queryKey: ["notifications", filters],
    queryFn: async () => {
      const fn = httpsCallable<unknown, { notifications: NotificationItem[]; total: number }>(
        functions, "listNotifications"
      );
      const result = await fn(filters ?? {});
      return result.data;
    },
    refetchInterval: 30000, // poll every 30s
  });
}

export function useNotificationCounts() {
  return useQuery({
    queryKey: ["notificationCounts"],
    queryFn: async () => {
      const fn = httpsCallable<void, NotificationCounts>(functions, "getNotificationCounts");
      const result = await fn();
      return result.data;
    },
    refetchInterval: 15000, // poll every 15s for badge
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { notificationId: string; orgId: string }) => {
      const fn = httpsCallable(functions, "markNotificationRead");
      await fn(params);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notificationCounts"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable(functions, "markAllNotificationsRead");
      await fn();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notificationCounts"] });
    },
  });
}
